<template>
  <div class="show_all_content_wrapper">
    <!-- Start:: Main Section -->
    <main>
      <!--  =========== Start:: Filter Form =========== -->
      <div
        class="filter_content_wrapper"
        :class="{ active: filterFormIsActive }"
      >
        <button
          class="filter_toggler"
          @click="filterFormIsActive = !filterFormIsActive"
        >
          <i class="fal fa-times"></i>
        </button>
        <div class="filter_title_wrapper">
          <h5>{{ $t("TITLES.searchBy") }}</h5>
        </div>
        <div class="filter_form_wrapper">
          <form @submit.prevent="submitFilterForm">
            <div class="row justify-content-center align-items-center w-100">
              <!-- Start:: Bundle Number Input -->
              <base-input
                col="3"
                type="text"
                :placeholder="$t('PLACEHOLDERS.bundle_number')"
                v-model.trim="filterOptions.bundle_number"
              />
              <!-- End:: Bundle Number Input -->

              <!-- Start:: Package Number Input -->
              <base-input
                col="3"
                type="text"
                :placeholder="$t('PLACEHOLDERS.package_number')"
                v-model.trim="filterOptions.search"
              />
              <!-- End:: Package Number Input -->

              <!-- Start:: Status Input -->
              <base-select-input
                col="3"
                :optionsList="bundleStatuses"
                :placeholder="$t('TABLES.Bundles.status')"
                v-model="filterOptions.status"
              />
              <!-- End:: Status Input -->

              <!-- Start:: Provider Input -->
              <base-select-input
                col="3"
                class="provider-select"
                :optionsList="providers"
                :placeholder="$t('TABLES.Bundles.provider_name')"
                v-model="filterOptions.provider_id"
                :loading="providersLoading"
                @search-change="onProviderSearch"
                @open="onProviderOpen"
              />
              <!-- End:: Provider Input -->

              <!-- Start:: Driver Input -->
              <base-select-input
                col="3"
                class="driver-select"
                :optionsList="drivers"
                :placeholder="$t('TABLES.Bundles.driver_name')"
                v-model="filterOptions.delivery_id"
                :loading="driversLoading"
                @search-change="onDriverSearch"
                @open="onDriverOpen"
              />
              <!-- End:: Driver Input -->

              <!-- Start:: Service Type Input -->
              <base-select-input
                col="3"
                :optionsList="serviceTypes"
                :placeholder="$t('TABLES.Bundles.service_type')"
                v-model="filterOptions.service_type"
              />
              <!-- End:: Service Type Input -->

              <!-- Start:: From Date Input -->
              <base-picker-input
                col="3"
                type="date"
                :placeholder="$t('PLACEHOLDERS.from_date')"
                v-model="filterOptions.from_date"
              />
              <!-- End:: From Date Input -->

              <!-- Start:: To Date Input -->
              <base-picker-input
                col="3"
                type="date"
                :placeholder="$t('PLACEHOLDERS.to_date')"
                v-model="filterOptions.to_date"
              />
              <!-- End:: To Date Input -->
            </div>

            <div class="btns_wrapper">
              <button class="submit_btn" :disabled="isWaitingRequest">
                <i class="fal fa-search"></i>
              </button>
              <button
                class="reset_btn"
                type="button"
                :disabled="isWaitingRequest"
                @click="resetFilter"
              >
                <i class="fal fa-redo"></i>
              </button>
            </div>
          </form>
        </div>
      </div>
      <!--  =========== End:: Filter Form =========== -->

      <!--  =========== Start:: Table Title =========== -->
      <div class="table_title_wrapper">
        <div class="title_text_wrapper">
          <h5>{{ $t("TITLES.bundles") }}</h5>
          <button
            v-if="!filterFormIsActive"
            class="filter_toggler"
            @click.stop="filterFormIsActive = !filterFormIsActive"
          >
            <i class="fal fa-search"></i>
          </button>
        </div>
      </div>
      <!--  =========== End:: Table Title =========== -->

      <!--  =========== Start:: Data Table =========== -->
      <v-data-table
        class="thumb"
        :loading="loading"
        :loading-text="$t('TABLES.loadingData')"
        :search="searchValue"
        :headers="tableHeaders"
        :items="tableRows"
        item-class="ltr"
        :items-per-page="paginations.items_per_page"
        hide-default-footer
      >
        <!-- Start:: No Data State -->
        <template v-slot:no-data>
          {{ $t("TABLES.noData") }}
        </template>
        <!-- End:: No Data State -->

        <!-- Start:: Serial Number -->
        <template v-slot:[`item.id`]="{ index }">
          {{
            (paginations.current_page - 1) * paginations.items_per_page +
            index +
            1
          }}
        </template>
        <!-- End:: Serial Number -->

        <!-- Start:: Status -->
        <template v-slot:[`item.status`]="{ item }">
          <v-chip small :color="getStatusColor(item.status)" text-color="white">
            {{ item.status_label }}
          </v-chip>
        </template>
        <!-- End:: Status -->

        <!-- Start:: Service Type -->
        <template v-slot:[`item.service_type`]="{ item }">
          <span v-if="item.service_type">{{ item.service_type.text }}</span>
          <span v-else>-</span>
        </template>
        <!-- End:: Service Type -->

        <!-- Start:: Driver Name -->
        <template v-slot:[`item.driver_name`]="{ item }">
          <span v-if="item.driver_name">{{ item.driver_name }}</span>
          <span v-else class="text-muted">-</span>
        </template>
        <!-- End:: Driver Name -->

        <!-- Start:: Delivery Prize -->
        <template v-slot:[`item.delivery_prize`]="{ item }">
          {{ item.delivery_prize }} {{ $t("PLACEHOLDERS.sar_currency") }}
        </template>
        <!-- End:: Delivery Prize -->

        <!-- Start:: Actions -->
        <template v-slot:[`item.actions`]="{ item }">
          <div class="actions">
            <a-tooltip placement="bottom">
              <template slot="title">
                <span>{{ $t("BUTTONS.show") }}</span>
              </template>
              <button class="btn_show" @click="showItem(item)">
                <i class="fal fa-eye"></i>
              </button>
            </a-tooltip>
          </div>
        </template>
        <!-- End:: Actions -->
      </v-data-table>
      <!--  =========== End:: Data Table =========== -->
    </main>
    <!-- End:: Main Section -->

    <!-- Start:: Pagination -->
    <template>
      <div class="pagination_container text-center mt-3 mb-0">
        <v-pagination
          class="py-0"
          square
          v-model="paginations.current_page"
          :length="paginations.last_page"
          :total-visible="6"
          @input="updateRouterQueryParam($event)"
          :prev-icon="
            getAppLocale == 'ar' ? 'fal fa-angle-right' : 'fal fa-angle-left'
          "
          :next-icon="
            getAppLocale == 'ar' ? 'fal fa-angle-left' : 'fal fa-angle-right'
          "
        />
      </div>
    </template>
    <!-- End:: Pagination -->
  </div>
</template>

<script>
import { mapGetters } from "vuex";

export default {
  name: "AllBundles",

  computed: {
    ...mapGetters({
      getAppLocale: "AppLangModule/getAppLocale",
    }),

    bundleStatuses() {
      return [
        { id: 1, name: this.$t("BUNDLE_STATUS.new"), value: "new" },
        { id: 2, name: this.$t("BUNDLE_STATUS.reserved"), value: "reserved" },
        { id: 3, name: this.$t("BUNDLE_STATUS.picked_up"), value: "picked_up" },
        {
          id: 4,
          name: this.$t("BUNDLE_STATUS.in_transit"),
          value: "in_transit",
        },
        { id: 5, name: this.$t("BUNDLE_STATUS.delivered"), value: "delivered" },
        { id: 6, name: this.$t("BUNDLE_STATUS.returned"), value: "returned" },
        { id: 7, name: this.$t("BUNDLE_STATUS.pending"), value: "pending" },
      ];
    },

    serviceTypes() {
      return [
        {
          id: 1,
          name: this.$t("PLACEHOLDERS.delivery_type_home"),
          value: "direct",
        },
        {
          id: 2,
          name: this.$t("PLACEHOLDERS.delivery_type_point"),
          value: "distribution_point",
        },
        {
          id: 3,
          name: this.$t("PLACEHOLDERS.mixed"),
          value: "mixed",
        },
      ];
    },
  },

  data() {
    return {
      // Start:: Loading Data
      loading: false,
      isWaitingRequest: false,
      // End:: Loading Data

      // Start:: Filter Data
      filterFormIsActive: false,
      filterOptions: {
        bundle_number: null,
        search: null,
        status: null,
        provider_id: null,
        delivery_id: null,
        service_type: null,
        from_date: null,
        to_date: null,
      },
      // End:: Filter Data

      // Start:: Dropdown Data
      providers: [],
      providersPage: 1,
      providersLastPage: 1,
      providersSearch: "",
      providersLoading: false,
      providersLoadingMore: false,
      providersHasMore: false,

      drivers: [],
      driversPage: 1,
      driversLastPage: 1,
      driversSearch: "",
      driversLoading: false,
      driversLoadingMore: false,
      driversHasMore: false,
      // End:: Dropdown Data

      // Start:: Table Data
      searchValue: "",
      tableHeaders: [
        {
          text: this.$t("TABLES.Bundles.serialNumber"),
          value: "id",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("TABLES.Bundles.bundle_number"),
          value: "bundle_number",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("TABLES.Bundles.provider_name"),
          value: "provider_name",
          sortable: false,
          align: "center",
        },

        {
          text: this.$t("TABLES.Bundles.driver_name"),
          value: "driver_name",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("TABLES.Bundles.delivery_prize"),
          value: "delivery_prize",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("TABLES.Bundles.created_at"),
          value: "created_at",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("TABLES.Bundles.service_type"),
          value: "service_type",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("TABLES.Bundles.status"),
          value: "status",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("TABLES.Bundles.actions"),
          value: "actions",
          sortable: false,
          align: "center",
        },
      ],
      tableRows: [],
      // End:: Table Data

      // Start:: Pagination Data
      paginations: {
        current_page: 1,
        last_page: 1,
        items_per_page: 10,
      },
      // End:: Pagination Data
    };
  },

  watch: {
    // Start:: Page Query Param Watcher To Get Page Data Based On It's Change
    ["$route.query.page"]() {
      this.paginations.current_page = +this.$route.query.page;
      this.setTableRows();
    },
    // End:: Page Query Param Watcher To Get Page Data Based On It's Change
  },

  methods: {
    // Start:: Get Status Color
    getStatusColor(status) {
      const colors = {
        new: "blue",
        searching_driver: "orange",
        assigned: "purple",
        picking_up: "cyan",
        picked_up: "cyan",
        in_transit: "indigo",
        delivered: "green",
        cancelled: "red",
        returned: "grey",
        pending: "yellow darken-2",
        reserved: "purple",
      };
      return colors[status] || "grey";
    },
    // End:: Get Status Color

    // Start:: Handeleing Filter Form
    async submitFilterForm() {
      if (this.$route.query.page !== "1") {
        await this.$router.push({
          path: "/bundles/all",
          query: { page: 1 },
        });
      }
      this.setTableRows();
    },

    async resetFilter() {
      this.filterOptions = {
        bundle_number: null,
        search: null,
        status: null,
        provider_id: null,
        delivery_id: null,
        service_type: null,
        from_date: null,
        to_date: null,
      };
      if (this.$route.query.page !== "1") {
        await this.$router.push({
          path: "/bundles/all",
          query: { page: 1 },
        });
      }
      this.setTableRows();
    },
    // End:: Handeleing Filter Form

    // Start:: Update Router Query Param
    updateRouterQueryParam(pagenationValue) {
      this.$router.push({
        query: {
          ...this.$route.query,
          page: pagenationValue,
        },
      });
    },
    // End:: Update Router Query Param

    // ============================================================
    // Start:: Providers Dropdown (infinite scroll + search)
    // ============================================================
    async getProviders(reset = false) {
      if (reset) {
        this.providersPage = 1;
        this.providers = [];
        this.providersHasMore = false;
      }
      const isFirstPage = this.providersPage === 1;
      if (isFirstPage) {
        this.providersLoading = true;
      } else {
        this.providersLoadingMore = true;
      }
      try {
        const res = await this.$axios({
          method: "GET",
          url: "providers",
          params: {
            per_page: 10,
            page: this.providersPage,
            search: this.providersSearch || undefined,
          },
        });
        const items = res.data.data.map((item) => ({
          id: item.id,
          name: item.name,
          value: item.id,
        }));
        this.providers = reset ? items : [...this.providers, ...items];
        this.providersLastPage = res.data.meta?.last_page ?? 1;
        this.providersHasMore = this.providersPage < this.providersLastPage;
      } catch (error) {
        console.log(error);
      } finally {
        this.providersLoading = false;
        this.providersLoadingMore = false;
      }
    },

    onProviderSearch(query) {
      this.providersSearch = query;
      clearTimeout(this._providerSearchTimer);
      this._providerSearchTimer = setTimeout(() => {
        this.getProviders(true).then(() => this.attachProviderScroll());
      }, 350);
    },

    onProviderOpen() {
      if (this.providers.length === 0) {
        this.getProviders(true).then(() => this.attachProviderScroll());
      } else {
        this.$nextTick(() => this.attachProviderScroll());
      }
    },

    attachProviderScroll() {
      // Remove old listener first
      if (this._providerScrollEl) {
        this._providerScrollEl.removeEventListener("scroll", this._providerScrollHandler);
        this._providerScrollEl = null;
      }
      if (!this.providersHasMore) return;
      this.$nextTick(() => {
        const wrapper = this.$el.querySelector(
          ".provider-select .multiselect__content-wrapper"
        );
        if (!wrapper) return;
        this._providerScrollEl = wrapper;
        this._providerScrollHandler = () => {
          const { scrollTop, scrollHeight, clientHeight } = wrapper;
          if (
            scrollHeight - scrollTop - clientHeight < 50 &&
            this.providersHasMore &&
            !this.providersLoadingMore
          ) {
            this.providersPage++;
            this.getProviders().then(() => {
              if (!this.providersHasMore && this._providerScrollEl) {
                this._providerScrollEl.removeEventListener("scroll", this._providerScrollHandler);
                this._providerScrollEl = null;
              }
            });
          }
        };
        wrapper.addEventListener("scroll", this._providerScrollHandler);
      });
    },
    // End:: Providers Dropdown

    // ============================================================
    // Start:: Drivers Dropdown (infinite scroll + search)
    // ============================================================
    async getDrivers(reset = false) {
      if (reset) {
        this.driversPage = 1;
        this.drivers = [];
        this.driversHasMore = false;
      }
      const isFirstPage = this.driversPage === 1;
      if (isFirstPage) {
        this.driversLoading = true;
      } else {
        this.driversLoadingMore = true;
      }
      try {
        const res = await this.$axios({
          method: "GET",
          url: "drivers",
          params: {
            per_page: 10,
            page: this.driversPage,
            is_approved_by_admin: 1,
            search: this.driversSearch || undefined,
          },
        });
        const items = res.data.data.map((item) => ({
          id: item.id,
          name: item.name,
          value: item.id,
        }));
        this.drivers = reset ? items : [...this.drivers, ...items];
        this.driversLastPage = res.data.meta?.last_page ?? 1;
        this.driversHasMore = this.driversPage < this.driversLastPage;
      } catch (error) {
        console.log(error);
      } finally {
        this.driversLoading = false;
        this.driversLoadingMore = false;
      }
    },

    onDriverSearch(query) {
      this.driversSearch = query;
      clearTimeout(this._driverSearchTimer);
      this._driverSearchTimer = setTimeout(() => {
        this.getDrivers(true).then(() => this.attachDriverScroll());
      }, 350);
    },

    onDriverOpen() {
      if (this.drivers.length === 0) {
        this.getDrivers(true).then(() => this.attachDriverScroll());
      } else {
        this.$nextTick(() => this.attachDriverScroll());
      }
    },

    attachDriverScroll() {
      // Remove old listener first
      if (this._driverScrollEl) {
        this._driverScrollEl.removeEventListener("scroll", this._driverScrollHandler);
        this._driverScrollEl = null;
      }
      if (!this.driversHasMore) return;
      this.$nextTick(() => {
        const wrapper = this.$el.querySelector(
          ".driver-select .multiselect__content-wrapper"
        );
        if (!wrapper) return;
        this._driverScrollEl = wrapper;
        this._driverScrollHandler = () => {
          const { scrollTop, scrollHeight, clientHeight } = wrapper;
          if (
            scrollHeight - scrollTop - clientHeight < 50 &&
            this.driversHasMore &&
            !this.driversLoadingMore
          ) {
            this.driversPage++;
            this.getDrivers().then(() => {
              if (!this.driversHasMore && this._driverScrollEl) {
                this._driverScrollEl.removeEventListener("scroll", this._driverScrollHandler);
                this._driverScrollEl = null;
              }
            });
          }
        };
        wrapper.addEventListener("scroll", this._driverScrollHandler);
      });
    },
    // End:: Drivers Dropdown

    // Start:: Set Table Rows
    async setTableRows() {
      this.loading = true;
      try {
        let res = await this.$axios({
          method: "GET",
          url: "bundles",
          params: {
            page: this.paginations.current_page,
            per_page: this.paginations.items_per_page,
            package_number: this.filterOptions.search,
            bundle_number: this.filterOptions.bundle_number,
            status: this.filterOptions.status?.value,
            provider_id: this.filterOptions.provider_id?.value,
            delivery_id: this.filterOptions.delivery_id?.value,
            service_type: this.filterOptions.service_type?.value,
            from_date: this.filterOptions.from_date,
            to_date: this.filterOptions.to_date,
          },
        });
        this.loading = false;
        this.tableRows = res.data.data;
        this.paginations.last_page = res.data.meta.last_page;
        this.paginations.items_per_page = res.data.meta.per_page;
      } catch (error) {
        this.loading = false;
        console.log(error.response?.data?.message);
      }
    },
    // End:: Set Table Rows

    // Start:: Show Item
    showItem(item) {
      this.$router.push({ path: `/bundles/show/${item.id}` });
    },
    // End:: Show Item
  },

  created() {
    // Start:: Fire Methods
    window.addEventListener("click", () => {
      this.filterFormIsActive = false;
    });
    if (this.$route.query.page) {
      this.paginations.current_page = +this.$route.query.page;
    }
    this.setTableRows();
    // End:: Fire Methods
  },

  beforeDestroy() {
    if (this._providerScrollEl) {
      this._providerScrollEl.removeEventListener("scroll", this._providerScrollHandler);
    }
    if (this._driverScrollEl) {
      this._driverScrollEl.removeEventListener("scroll", this._driverScrollHandler);
    }
  },
};
</script>
